import React, {useCallback, useContext} from 'react'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'

import {Button} from '@vio/atlas/Button'
import {Icon} from '@vio/atlas/Icon'
import {cssTheme} from '@vio/atlas/themes'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {navigateTo} from '@vio/core/src/_web/utils/browser/navigateTo'
import {isApple} from '@vio/core/src/_web/utils/userAgent'
import {
  getOnAuthSuccessLandingUrl,
  removeOnAuthSuccessLandingUrl
} from '@vio/core/src/auth/business/persistData'
import {
  getAuthenticationFlow,
  getSusiAnalyticsData
} from '@vio/core/src/auth/modules/selectors'
import {
  AuthenticationFlows,
  AuthenticationMethod
} from '@vio/core/src/auth/types/Auth'
import {AuthenticationProvider} from '@vio/core/src/auth/types/Cognito'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {SusiContext} from '../SusiProvider'
import {HandleLogin} from '../types'

interface Props {
  handleLogin: HandleLogin
  handleEmailLogin?: (e: React.SyntheticEvent) => void
  handleOnClose?: () => void
}

const ButtonsWrapper = styled.div`
  > button + button {
    margin-top: ${cssTheme.layout.spacing.s400};
  }
`

export const SignInButtons = ({
  handleLogin,
  handleEmailLogin,
  handleOnClose
}: Props) => {
  const {setSkipCloseAnimation} = useContext(SusiContext)
  const analyticsData = useSelector(getSusiAnalyticsData)
  const isAppleDevice = isApple()
  const successLandingUrl = getOnAuthSuccessLandingUrl()
  const authenticationFlow = useSelector(getAuthenticationFlow)
  const isPreCheckout = authenticationFlow === AuthenticationFlows.PreCheckout
  const {brandCode, brandName} = useBrand()
  const {
    auth: {
      hasEmailOtpSignup,
      hasOidcProvider,
      showSignInToComplete,
      requiresSignInForCheckout
    }
  } = useBrandCapabilities()

  const handleContinueAsGuest = useCallback(() => {
    // If we don't have a success landing URL, simply close the sheet
    if (!successLandingUrl) {
      handleOnClose?.()
      return
    }

    // Skip close animation so BF-cache snapshot won't show the sheet
    setSkipCloseAnimation(true)
    handleOnClose?.()
    removeOnAuthSuccessLandingUrl()
    navigateTo(successLandingUrl)
  }, [successLandingUrl, handleOnClose, setSkipCloseAnimation])

  if (hasOidcProvider) {
    const mergedAnalyticsData = {
      ...analyticsData,
      authenticationMethod: brandCode
    }

    return (
      <ButtonsWrapper>
        <Button
          fullWidth
          dataId="SocialLoginOidc"
          size="xl"
          iconEnd={<Icon name="ChevronRight" size="md" />}
          variant="primary"
          onClick={handleLogin(
            AuthenticationMethod.OIDCFederation,
            AuthenticationProvider.OidcFederation,
            mergedAnalyticsData
          )}
        >
          <FormattedMessageWrapper
            id="SUSI.continueWith"
            defaultMessage="Continue with {channel}"
            values={{channel: brandName}}
          />
        </Button>
      </ButtonsWrapper>
    )
  }

  return (
    <ButtonsWrapper>
      {isAppleDevice && (
        <Button
          fullWidth
          dataId="SocialLoginApple"
          size="xl"
          iconStart={<Icon name="Apple" />}
          variant="tertiary"
          onClick={handleLogin(
            AuthenticationMethod.Apple,
            AuthenticationProvider.Apple,
            analyticsData
          )}
        >
          <FormattedMessageWrapper
            id="SUSI.continueWith"
            defaultMessage="Continue with {channel}"
            values={{channel: 'Apple'}}
          />
        </Button>
      )}

      <Button
        fullWidth
        variant="tertiary"
        dataId="SocialLoginGoogle"
        size="xl"
        iconStart={<Icon name="GoogleColor" />}
        onClick={handleLogin(
          AuthenticationMethod.Google,
          AuthenticationProvider.Google,
          analyticsData
        )}
      >
        <FormattedMessageWrapper
          id="SUSI.continueWith"
          defaultMessage="Continue with {channel}"
          values={{channel: 'Google'}}
        />
      </Button>

      <Button
        fullWidth
        variant="tertiary"
        dataId="SocialLoginFacebook"
        size="xl"
        iconStart={<Icon name="FacebookColor" />}
        onClick={handleLogin(
          AuthenticationMethod.Facebook,
          AuthenticationProvider.Facebook,
          analyticsData
        )}
      >
        <FormattedMessageWrapper
          id="SUSI.continueWith"
          defaultMessage="Continue with {channel}"
          values={{channel: 'Facebook'}}
        />
      </Button>

      {hasEmailOtpSignup && !!handleEmailLogin && (
        <Button
          fullWidth
          variant="tertiary"
          dataId="SocialLoginEmail"
          size="xl"
          iconStart={<Icon name="Mail" />}
          onClick={handleEmailLogin}
        >
          <FormattedMessageWrapper
            id="SUSI.continueWith"
            defaultMessage="Continue with {channel}"
            values={{channel: 'Email'}}
          />
        </Button>
      )}

      {isPreCheckout && showSignInToComplete && !requiresSignInForCheckout && (
        <Button
          fullWidth
          variant="ghost"
          dataId="ContinueAsGuest"
          size="xl"
          onClick={handleContinueAsGuest}
        >
          <FormattedMessageWrapper
            id="SUSI.continueAsGuest"
            defaultMessage="Continue as guest"
          />
        </Button>
      )}
    </ButtonsWrapper>
  )
}
